'use client'

import React from 'react'
import Link from 'next/link'
import { Button, type ButtonProps } from '@/ui/atoms/Button'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'
import { useMemberFileHref } from '@/lib/downloads/useFilePass'
import { fileNameFromUrl } from '@/lib/downloads/fileLinks'

type Props = {
  href: string
  className?: string
  size?: ButtonProps['size'] | null
  variant?: ButtonProps['variant']
  children: React.ReactNode
}

/**
 * CMS button whose target is one of our files (e.g. the "Download the white paper" CTA on
 * the Hermes toolkit, 2026-09-17). Same gate as PdfCard: logged-out visitors go to
 * /login?next=<here>; members get the file and the lead notification fires. Renders the
 * logged-out variant on the server, so anonymous HTML never carries the raw file href.
 * Members get the file OPENED in a new tab (like PdfCard's "View"): a `download` attribute
 * made a 26 MB PDF save silently in the background and the button looked dead (2026-09-17).
 */
export const GatedFileButton: React.FC<Props> = ({ href, className, size, variant, children }) => {
  const { user, isLoaded, loginHref, notifyDownload, trackGateClick, trackFileOpen } =
    useDownloadGate()
  // carries the member's file pass (see lib/downloads/filePass.ts)
  const memberHref = useMemberFileHref(href)
  const fileName = fileNameFromUrl(href)

  return (
    <Button asChild className={className} size={size} variant={variant}>
      {user ? (
        <a
          href={memberHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            notifyDownload({ pdfFilename: fileName, pdfUrl: href })
            trackFileOpen(fileName, href, 'cta_button')
          }}
        >
          {children}
        </a>
      ) : (
        <Link
          href={loginHref}
          onClick={(e) => {
            // Session not known yet (the page is still hydrating): hold the tap instead of
            // sending a member to the login page.
            if (!isLoaded) return e.preventDefault()
            trackGateClick(fileName, 'cta_button')
          }}
        >
          {children}
        </Link>
      )}
    </Button>
  )
}
